import { ArrowRight, Cpu, Award, Zap, Settings, Layers, Search, PiggyBank } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImageAvif1920 from "@/assets/hero-truck-1920.avif";
import heroImageAvif1280 from "@/assets/hero-truck-1280.avif";
import heroImageAvif640 from "@/assets/hero-truck-640.avif";
import heroImageJpg1920 from "@/assets/hero-truck-1920.jpg";
import heroImageJpg1280 from "@/assets/hero-truck-1280.jpg";
import heroImageJpg640 from "@/assets/hero-truck-640.jpg";
import { useLanguage } from "@/i18n/LanguageContext";

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    { icon: Award, title: t("hero.experience"), sub: t("hero.experience.sub") },
    { icon: Zap, title: t("hero.diagnostics"), sub: t("hero.diagnostics.sub") },
    { icon: Settings, title: t("hero.tools"), sub: t("hero.tools.sub") },
    { icon: Layers, title: t("hero.electronics"), sub: t("hero.electronics.sub") },
    { icon: Search, title: t("hero.analysis"), sub: t("hero.analysis.sub") },
    { icon: PiggyBank, title: t("hero.repairs"), sub: t("hero.repairs.sub") },
  ];

  return (
    <section id="home" className="relative min-h-screen flex flex-col justify-center py-20 lg:py-32">
      <div className="absolute inset-0 z-0">
        <picture>
          <source
              type="image/avif"
              srcSet={`${heroImageAvif640} 640w, ${heroImageAvif1280} 1280w, ${heroImageAvif1920} 1920w`}
              sizes="100vw"
            />

            <source
              type="image/jpeg"
              srcSet={`${heroImageJpg640} 640w, ${heroImageJpg1280} 1280w, ${heroImageJpg1920} 1920w`}
              sizes="100vw"
            />

          <img
            src={heroImageJpg1280}
            alt="Diagnostyka i naprawa sterowników - Gendera Service"
            className="w-full h-full object-cover"
            width={1920}
            height={1280}
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full mb-6 animate-fade-in">
            <Cpu className="w-5 h-5" size={20} aria-hidden="true" />
            <span className="text-sm font-medium">{t("hero.badge")}</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight animate-fade-in" style={{ animationDelay: "0.1s" }}>
            {t("hero.title1")}<br />
            <span className="text-accent">{t("hero.title2")}</span>
          </h1>

          <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: "0.2s" }}>
            {t("hero.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <Button size="lg" className="bg-accent hover:bg-orange-hover text-accent-foreground font-semibold px-8 py-6 text-lg" onClick={() => scrollToSection('contact')}>
              {t("hero.cta")}
              <ArrowRight className="ml-2 w-5 h-5" size={20} aria-hidden="true" />
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 px-8 py-6 text-lg" onClick={() => scrollToSection('services')}>
              {t("hero.services")}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in" style={{ animationDelay: "0.4s" }}>
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-center gap-3 text-primary-foreground">
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-accent" size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{f.title}</p>
                    <p className="text-xs text-primary-foreground/70">{f.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:flex">
        <div className="w-8 h-12 rounded-full border-2 border-primary-foreground/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-accent rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
